import React, { useEffect } from "react";
import cn from "classnames";
import * as yup from "yup";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";
import { selectInvestmentStepData, selectIsInvestmentCreated, selectIsInvestmentLoading } from "store/user/investment/selectors";
import { createStructuredSelector } from "reselect";
import { createInvestMentDataStart } from "store/user/investment/actions";
import { compose } from "recompose";
import { connect } from "react-redux";
import { withFormik } from "formik";

import NextButton from "components/NextButton";
import PrevButton from "components/PrevButton";
import { withRouter } from "next/router";
import { selectProjectDetails } from "store/project/selectors";
import { selectEmail, selectUserDetails, selectUserId } from "store/user/account/selectors";
import Checkbox from "components/Checkbox";
import { fetchUserDetailsSuccess } from "store/user/account/actions";
import { TENANTS_LIST } from "constants/index";
import { selectTenant } from "store/app/selectors";
import { checkGTM } from "utils";

const messages = {
    investmentOverviewCheckboxValidationRequired: "agree_to_invest",
    buttonText: "buy_now_text",
    loadingButtonText: "please_wait_button",
    depositDigitalAssets: "deposit_digital_assets",
    whatIsWallet: "what_is_wallet",
    whatIsWalletDescription: "what_is_wallet_description",
    oneClickSetupLicensed: "one_click_setup_licensed",
    yourSecuritiesManaged: "your_securities_managed",
};

const InvestmentTanganyData = ({
    backSlideEffect,
    setBackSlideEffect,
    setInvestmentStepData,
    isInvestmentLoading,
    errors,
    touched,
    values,
    handleSubmit,
    validateForm,
    isInvestmentCreated,
    setFieldValue,
    userData,
    fetchUserDetailsSuccess,
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    useEffect(() => {
        validateForm();
    }, []);

    useEffect(() => {
        if (isInvestmentCreated === true) {
            if (values.IsTanganyCreateWalletConfirmed) {
                fetchUserDetailsSuccess({
                    ...userData,
                    IsTanganyCreateWalletConfirmed: values.IsTanganyCreateWalletConfirmed,
                });
            }

            setInvestmentStepData({ overviewData: { step: "investmentSuccess", data: values } });
            setBackSlideEffect("step-slide-left");
        }
    }, [isInvestmentCreated]);

    return (
        <>
            <div className={`${backSlideEffect}`}>
                <h4>{formatMessage(messages.depositDigitalAssets)}</h4>
                <h5>{formatMessage(messages.whatIsWallet)}</h5>
                <p>{formatMessage(messages.whatIsWalletDescription)}</p>
                <div className="check-section">
                    <Checkbox
                        className="mt-6"
                        inputId="IsTanganyCreateWalletConfirmed"
                        name="IsTanganyCreateWalletConfirmed"
                        value={values.IsTanganyCreateWalletConfirmed}
                        label={formatMessage(messages.oneClickSetupLicensed)}
                        labelClassName="fw-600"
                        onChange={() => {
                            setFieldValue("IsTanganyCreateWalletConfirmed", !values.IsTanganyCreateWalletConfirmed);
                        }}
                        isError={touched.IsTanganyCreateWalletConfirmed && errors.IsTanganyCreateWalletConfirmed}
                    />
                    <p className="ml-10 mt-2">{formatMessage(messages.yourSecuritiesManaged)}</p>
                </div>
            </div>
            <div className="bottom-bar-container">
                <PrevButton
                    onClick={() => {
                        setInvestmentStepData({ overviewData: { step: "investmentOverviewData" } });
                        setBackSlideEffect("step-slide-right");
                    }}
                />
                <NextButton
                    className={cn({
                        "mw-fit": true,
                        "disable-grey": Object.keys(errors).length,
                        "disable-pointer": isInvestmentLoading,
                        "create-investment-button": true,
                    })}
                    title={isInvestmentLoading ? formatMessage(messages.loadingButtonText) : formatMessage(messages.buttonText)}
                    onClick={handleSubmit}
                    isLoading={isInvestmentLoading}
                />
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    isInvestmentLoading: selectIsInvestmentLoading,
    isInvestmentCreated: selectIsInvestmentCreated,
    projectData: selectProjectDetails,
    userEmail: selectEmail,
    investmentStepData: selectInvestmentStepData,
    userUid: selectUserId,
    userData: selectUserDetails,
    currentTenant: selectTenant,
});

const mapDispatchToProps = (dispatch) => ({
    createInvestMentDataStart: (data) => dispatch(createInvestMentDataStart(data)),
    fetchUserDetailsSuccess: (data) => dispatch(fetchUserDetailsSuccess(data)),
});

export default compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    withFormik({
        mapPropsToValues: () => {
            return {
                IsTanganyCreateWalletConfirmed: false,
            };
        },
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: () =>
            yup.object().shape({
                IsTanganyCreateWalletConfirmed: yup
                    .bool()
                    .required("investmentOverviewCheckboxValidationRequired")
                    .oneOf([true], "investmentOverviewCheckboxValidationRequired"),
            }),
        handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
            const { investmentStepData, router, projectData, userUid, userEmail, createInvestMentDataStart, currentTenant } =
                props;

            if (currentTenant === TENANTS_LIST.SACHWERTINVEST) {
                checkGTM();
                window?.dataLayer?.push({ event: "swi_sucessfull_invest_process" });
            }

            setErrors({ requestError: undefined });

            setSubmitting(false);

            const projectID = router.query?.id;
            const overviewData = investmentStepData?.investmentData?.overviewData?.data;

            const isValidProjectData = projectID && Array.isArray(projectData) && projectData.length;

            if (isValidProjectData) {
                const project = projectData.find((item) => item.projectID === projectID);
                if (project) {
                    const { investmentID, IsXPConfirmed, IsMissingXPConfirmed } = investmentStepData.investmentData;
                    const investmentData = {
                        ...overviewData,
                        User: {
                            uid: userUid,
                            ...values,
                        },
                        UserEmail: userEmail,
                        ProjectID: project.projectID,
                        uid: investmentID,
                        Amount: `${investmentStepData.investSum.sum}`,
                        IsXPConfirmed,
                        IsMissingXPConfirmed,
                    };
                    createInvestMentDataStart && createInvestMentDataStart({ investment: { ...investmentData } });
                }
            }
        },
        displayName: "InvestmentTanganyData",
    }),
)(InvestmentTanganyData);
